<template>
  <div :class="containerClass">
    <div class="ly-row ly-start-center mb-1 w-80">
      <slot v-if="showResults" name="results" :total-results="totalResults">
        <p class="m-0 text-uppercase font-weight-bold">
          {{ formatInteger(totalResults) }} Results
        </p>
      </slot>
      <slot v-if="showFixedHeader" name="fixedHeader"></slot>
    </div>
    <div class="scroller-h" :class="mainSectionClass">
      <table
        class="datatable shadow-sm"
        :class="{
          'table-condensed': condensed,
          expanded: expandedItemId,
          [tableClass]: tableClass,
        }"
      >
        <thead class="p-relative">
          <tr
            class="ua-datatable-result-row"
            data-ua-multiple-id="ua-datatable-result-row"
          >
            <th v-if="enableRowSelection" class="table-checkbox-column">
              <lp-checkbox
                v-if="!enableSingleSelection"
                :model-value="pageRowsSelectionState !== SelectAllState.None"
                :indeterminate="
                  pageRowsSelectionState === SelectAllState.Some ||
                  (selectedItems.size > 0 &&
                    pageRowsSelectionState !== SelectAllState.All)
                "
                @change="selectAllPageItems"
              />
            </th>
            <th
              v-for="col in columns"
              :key="col.name"
              :data-ua-data-table-header-id="col.name"
              :style="{
                textAlign: col.alignLabel || 'left',
                width: getColumnWidth(col.width),
                wordWrap: 'break-word',
                whiteSpace: 'normal',
              }"
              @click="col.sortable ? changeSort(col.sortBy || col.name) : null"
            >
              <div class="d-flex">
                {{ col.translation ?? col.label }}
                <lp-icon
                  v-show="col.sortable"
                  :name="getColumnIcon(col.sortBy || col.name)"
                  class="d-flex ly-center-center"
                />
              </div>
            </th>
            <th
              v-if="enableExpandAction"
              :style="{
                width: getColumnWidth(expandableColumnWidth),
              }"
            ></th>
          </tr>
        </thead>
        <tbody v-if="isLoading">
          <tr>
            <td colspan="100%">
              <div class="ly-center-center" style="display: flex">
                <lp-spinner type="inline" />
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="totalResults">
          <template v-for="row in computedData" :key="row.id">
            <tr
              :class="isRowSelected(row.id) + rowClass"
              :data-ua-row-id="row.id"
              @click="$emit('onExpand', row.id, row)"
            >
              <td v-if="enableRowSelection" class="td-selection">
                <lp-radio
                  v-if="enableRowSelection && enableSingleSelection"
                  :model-value="singleSelection"
                  :value="row.id"
                  :disabled="disableCherryPickSelection.has(row.id)"
                  @update:model-value="onSingleSelection"
                />
                <Checkbox
                  v-else
                  :model-value="cherryPickSelection.has(row.id)"
                  :is-disabled="
                    disableCherryPickSelection.has(row.id) ||
                    (!cherryPickSelection.has(row.id) && selectLimitReached)
                  "
                  :tooltip-message="tooltipDisableCheckbox"
                  @change="onMultipleSelection(row.id, row)"
                />
              </td>
              <td
                v-for="col in columns"
                :key="col.name"
                :style="{
                  textAlign: col.alignContent || 'left',
                  width: getColumnWidth(col.width),
                }"
                class="td-column"
                @click="() => $emit('onSelect', row)"
              >
                <slot :name="col.name" :field="row[col.name]" :row="row">
                  {{ row[col.name] }}
                </slot>
              </td>
              <td v-if="enableExpandAction" :style="{ textAlign: 'right' }">
                <slot name="expand-action" :row="row">
                  <i class="lpi-chevron-right"></i>
                </slot>
              </td>
            </tr>
            <slot name="after-row" :row="row"></slot>
          </template>
        </tbody>
        <tbody v-else>
          <!--  NO RESULTS -->
          <tr>
            <td colspan="100%" class="bg-gray-light">
              <p
                class="text-center text-uppercase font-weight-bold text-dark-muted"
              >
                No Results
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- PAGINATION -->
    <div
      v-if="pagination && showPagination"
      id="ua-data-table-pagination"
      class="ly-row ly-spacebetween-center"
      :class="paginationClass"
    >
      <div class="ly-row">
        <div v-if="showPageSizeSelector" class="ly-row ly-center-center">
          <span>Show</span>
          <lp-select
            id="ua-pagination-select-page-size"
            :model-value="pageSize"
            :options="RECORDS_PER_PAGE"
            placement="top"
            compact
            :disabled="paginationDisabled"
            @update:model-value="(_, model) => onPageSizeChange(model.value)"
          />
          <span>Items</span>
        </div>
      </div>

      <div class="ly-row ly-end-center">
        <span>{{ paginationString }}</span>
        <lp-button
          data-ua-unique-id="ua-pagination-previous-page"
          class="btn btn-icon text-hover-blue xpy-2 xpx-1.5 xmr-2"
          icon="chevron-left"
          :disabled="!pagination.prevPage.value || paginationDisabled"
          @click="onPageChange(pagination.prevPage.value)"
        />
        <lp-button
          data-ua-unique-id="ua-data-table-pagination-next-page-button"
          class="btn btn-icon text-hover-blue xpy-2 xpx-1.5"
          icon="chevron-right"
          :disabled="!pagination.nextPage.value || paginationDisabled"
          @click="onPageChange(pagination.nextPage.value)"
        />
      </div>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="DataType extends GenericDataType = GenericDataType"
>
import { toRefs, ref, watch, computed } from 'vue'

import difference from 'lodash/difference'

import { formatInteger } from '@/helpers/numbers'

import useColumnSort from './use-column-sort'
import usePagination from './use-pagination'

import type { DataTableProps, GenericDataType } from './types'
import { SelectAllBehavior, SelectAllState } from './types'

import Checkbox from './Checkbox.vue'
import LpButton from '@/components/button'
import { t } from '@/plugins/i18n'

defineOptions({
  name: 'lp-data-table',
})

const props = withDefaults(defineProps<DataTableProps<DataType>>(), {
  sortOptions: null,
  isLoading: false,
  showFixedHeader: false,
  rowSelection: () => [],
  rowDisableSelection: () => [],
  enableRowSelection: true,
  showPagination: true,
  showResults: true,
  showPageSizeSelector: true,
  expandedItemId: null,
  enableExpandAction: false,
  condensed: false,
  tooltipDisableCheckbox: t('Shared.Disabled'),
  rowClass: '',
  enableSingleSelection: false,
  expandableColumnWidth: null,
  keepVisibleSelectedItems: false,
  selectedItems: () => new Set([]),
  mainSectionClass: '',
  tableClass: '',
  paginationClass: '',
  containerClass: '',
  selectAllBehavior: SelectAllBehavior.AllInPage,
  rowSelectionLimit: undefined,
})

const emit = defineEmits([
  'onSelect',
  'onSort',
  'onPageChange',
  'onPageSizeChange',
  'onRowSelectionChange',
  'onExpand', // NOTE: Alias for onSelect
  'onSelectAll',
])
const {
  sortOptions,
  page,
  totalResults,
  pageSize,
  data,
  rowSelection,
  rowDisableSelection,
  enableSingleSelection,
  selectedItems,
  selectAllBehavior,
  expandedItemId,
  rowSelectionLimit,
} = toRefs(props)
const { changeSort, getColumnIcon } = useColumnSort(sortOptions, emit)
const cherryPickSelection = ref(new Set())
const disableCherryPickSelection = ref(new Set())
const singleSelection = ref<string | null>(null)
const paginationDisabled = ref(false)
const pagination = usePagination(page, totalResults, pageSize, onPageChange)

const currentPageIds = computed(() => {
  return data.value.map((item) => item.id as number)
})

const availableCurrentPageIds = computed(() => {
  return difference(currentPageIds.value, rowDisableSelection.value as number[])
})

// This computed returns the state of the row selection of the page
// All page rows selected = SelectAllState.All
// Some page rows selected = SelectAllState.Some
// None page rows selected = SelectAllState.None
const pageRowsSelectionState = computed(() => {
  const matches = availableCurrentPageIds.value.filter((id: number) => {
    return cherryPickSelection.value.has(id)
  })

  const localResult =
    matches.length === 0
      ? SelectAllState.None
      : matches.length === availableCurrentPageIds.value.length
        ? SelectAllState.All
        : SelectAllState.Some

  if (localResult === SelectAllState.None) {
    if (selectedItems.value.size > 0) {
      return SelectAllState.Some
    }
  }

  return localResult
})

watch(
  rowSelection.value as number[],
  (newSelection) => {
    if (enableSingleSelection.value) {
      singleSelection.value = String(newSelection[0]) ?? null
    }

    cherryPickSelection.value = new Set(newSelection)
  },
  { immediate: true },
)

watch(
  rowDisableSelection.value as number[],
  (newValue) => {
    disableCherryPickSelection.value = new Set(newValue)
  },
  { immediate: true },
)

function onPageChange(page: number | null) {
  emit('onPageChange', page)
}

function onPageSizeChange(newPageSize: number) {
  emit('onPageSizeChange', newPageSize)
}

const RECORDS_PER_PAGE = [
  { name: '10', value: 10 },
  { name: '25', value: 25 },
  { name: '50', value: 50 },
]

function onMultipleSelection(
  id: string | number,
  row: Record<string, unknown>,
) {
  if (cherryPickSelection.value.has(id)) {
    cherryPickSelection.value.delete(id)
    if (props.keepVisibleSelectedItems) {
      selectedItems.value.delete(row)
    }
  } else {
    if (selectLimitReached.value) {
      return false
    }
    cherryPickSelection.value.add(id)
    if (props.keepVisibleSelectedItems) {
      selectedItems.value.add(row)
    }
  }
  emit('onRowSelectionChange', [...cherryPickSelection.value], selectedItems)
}

function onSingleSelection(value: string) {
  singleSelection.value = value
  emit('onRowSelectionChange', [value])
}

function selectAllPageItems() {
  const ids = availableCurrentPageIds.value

  if (pageRowsSelectionState.value !== SelectAllState.All) {
    ids.forEach((id) => {
      if (selectLimitReached.value) {
        return false
      }
      cherryPickSelection.value.add(id)
    })
    if (props.keepVisibleSelectedItems) {
      props.data.forEach((item) => {
        if (selectLimitReached.value) {
          return false
        }
        selectedItems.value.add(item)
      })
    }

    if (selectAllBehavior.value === SelectAllBehavior.AllData) {
      emit('onSelectAll', true)
      paginationDisabled.value = true
    }
  } else {
    ids.forEach((id) => {
      cherryPickSelection.value.delete(id)
    })
    if (props.keepVisibleSelectedItems) {
      selectedItems.value.clear()
    }

    if (selectAllBehavior.value === SelectAllBehavior.AllData) {
      emit('onSelectAll', false)
      paginationDisabled.value = false
    }
  }

  emit('onRowSelectionChange', [...cherryPickSelection.value])
}

function isRowSelected(rowId: number) {
  if (rowId === expandedItemId.value) {
    return 'bg-gray-light'
  }
  return ''
}

function getColumnWidth(width: number | null | undefined): string {
  return width ? `${width}%` : 'auto'
}

const computedData = computed(() => {
  if (props.keepVisibleSelectedItems) {
    const selectedData = Array.from(selectedItems.value.values()) as DataType[]

    const propsDataMap = new Map(
      props.data.map((item: DataType) => [item.id, item]),
    )

    // Filter out items from selectedData that already exist in props.data by 'id'
    const filteredSelectedData = selectedData.filter(
      (item) => !propsDataMap.has(item.id),
    )

    // Combine filtered selectedData and props.data (props.data takes priority)
    return [...filteredSelectedData, ...data.value]
  } else {
    return data.value
  }
})

const paginationString = computed(() => {
  const startRecord = (page.value - 1) * pageSize.value + 1
  const endRecord = Math.min(page.value * pageSize.value, totalResults.value)

  return `Showing ${startRecord}-${endRecord} of ${totalResults.value}`
})

const selectLimitReached = computed(() => {
  return (
    rowSelectionLimit.value !== undefined &&
    (cherryPickSelection.value.size >= rowSelectionLimit.value ||
      (props.keepVisibleSelectedItems &&
        selectedItems.value.size >= rowSelectionLimit.value))
  )
})
</script>
<style scoped>
.table-checkbox-column {
  width: 72px;
}

.table-checkbox-column .lp-checkbox-label-group {
  padding: 0;
}
</style>
